import React, { useState, useCallback } from 'react';
import BaseTicketScreen from './BaseTicketScreen';
import {
  FormRow,
  FormGroup,
  Label,
  Input,
  Select,
  FieldError,
  AutocompleteWrapper,
  AutocompleteDropdown,
  SuggestionItem,
  SuggestionCusip,
  SuggestionSecurity,
  TicketSectionTitle,
  FullWidthSection,
  ExpanderHeader,
  ExpanderContent,
} from './shared/TicketStyledComponents';
import ExecutionClearingSection, { getTicketExecutionClearingConfig } from './shared/ExecutionClearingSection';
import { getTodayDate } from '../../utils/dateUtils';
import { calendarsFor } from '../../utils/businessDate';
import { provisionalSettleDate, resolveSettleDate, settlementCalendars } from '../../utils/settlement';
import { formatAmountWithCommas, parseAmountShorthand } from '../../utils/amountUtils';
import { useTenantConfig } from '../../contexts/TenantConfigContext';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import styled from 'styled-components';
import {
  Activity,
  ArrowRightLeft,
  Calculator,
  ChevronDown,
  ChevronRight,
  Database,
  Gauge,
  Info,
  Landmark,
  Percent,
  RefreshCw,
  ShieldCheck,
  TrendingUp,
} from 'lucide-react';


import { DateField } from '../shared/DateField';

const DurationImpactPanel = styled.div`
  margin-top: 0;
  padding: 18px;
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.78) 0%, rgba(8, 13, 23, 0.92) 100%);
  border-radius: 14px;
  border: 1px solid rgba(51, 65, 85, 0.74);
`;

const DurationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 10px;
  margin-bottom: 16px;
`;

const DurationCard = styled.div`
  background: rgba(15, 23, 42, 0.66);
  padding: 14px;
  border-radius: 10px;
  border: 1px solid rgba(71, 85, 105, 0.38);
  min-width: 0;
`;

const DurationLabel = styled.div`
  font-size: 10px;
  font-weight: 800;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  margin-bottom: 6px;
`;

const DurationValue = styled.div<{ positive?: boolean; negative?: boolean }>`
  font-size: 20px;
  font-weight: 900;
  color: ${props => {
    if (props.positive) return '#4ade80';
    if (props.negative) return '#f87171';
    return '#f8fafc';
  }};
  overflow-wrap: anywhere;
`;

const DurationChange = styled.div<{ positive?: boolean; negative?: boolean }>`
  font-size: 12px;
  font-weight: 800;
  color: ${props => {
    if (props.positive) return '#4ade80';
    if (props.negative) return '#f87171';
    return '#94a3b8';
  }};
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &::before {
    content: '${props => {
      if (props.positive) return '+';
      if (props.negative) return '-';
      return '~';
    }}';
  }
`;

const LoadingMessage = styled.div`
  color: #94a3b8;
  font-size: 12px;
  font-weight: 700;
`;

const ErrorMessage = styled.div`
  color: #fca5a5;
  font-size: 12px;
  font-weight: 700;
  padding: 11px 13px;
  background: rgba(127, 29, 29, 0.16);
  border-radius: 10px;
  border: 1px solid rgba(239, 68, 68, 0.36);
`;

const PriceSourceBadge = styled.span<{ source?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => {
    // The trader's own price, or a market source (Treasury.gov yield curve, FINRA TRACE). The
    // backend never returns an assumed price, so there is no third case.
    if (props.source === 'Provided') {
      return `
        background: rgba(56, 189, 248, 0.12);
        color: #7dd3fc;
        border: 1px solid rgba(56, 189, 248, 0.32);
      `;
    }
    return `
      background: rgba(34, 197, 94, 0.12);
      color: #86efac;
      border: 1px solid rgba(34, 197, 94, 0.32);
    `;
  }}
`;

const PriceSourceCard = styled(DurationCard)`
  background: linear-gradient(135deg, rgba(8, 47, 73, 0.28) 0%, rgba(15, 23, 42, 0.72) 100%);
  border: 1px solid rgba(56, 189, 248, 0.24);
`;

// IBOR Pricing Components
const PricingPanel = styled.div`
  margin-top: 0;
  padding: 18px;
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.78) 0%, rgba(8, 13, 23, 0.92) 100%);
  border-radius: 14px;
  border: 1px solid rgba(51, 65, 85, 0.74);
`;

const PricingTitle = styled.h3`
  font-size: 13px;
  font-weight: 900;
  color: #e2e8f0;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px;
  margin-bottom: 16px;
`;

const PricingCard = styled.div`
  background: rgba(15, 23, 42, 0.66);
  padding: 14px;
  border-radius: 10px;
  border: 1px solid rgba(71, 85, 105, 0.38);
  min-width: 0;
`;

const PricingLabel = styled.div`
  font-size: 10px;
  font-weight: 800;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  margin-bottom: 6px;
`;

const PricingValue = styled.div<{ highlight?: boolean }>`
  font-size: 20px;
  font-weight: 900;
  color: ${props => props.highlight ? '#34d399' : '#f8fafc'};
  overflow-wrap: anywhere;
`;

const PricingSubValue = styled.div`
  font-size: 12px;
  color: #94a3b8;
  margin-top: 4px;
`;

const PricingToggle = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const ToggleOption = styled.button<{ active?: boolean }>`
  min-height: 38px;
  padding: 8px 14px;
  border: 1px solid ${props => props.active ? 'rgba(52, 211, 153, 0.46)' : 'rgba(71, 85, 105, 0.44)'};
  background: ${props => props.active ? 'rgba(16, 185, 129, 0.16)' : 'rgba(15, 23, 42, 0.58)'};
  color: ${props => props.active ? '#34d399' : '#cbd5e1'};
  border-radius: 10px;
  font-weight: 800;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    border-color: rgba(52, 211, 153, 0.6);
    color: #34d399;
  }
`;

const InputWithCalc = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, 1fr) auto;
  gap: 10px;
  align-items: center;

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const CalcButton = styled.button`
  min-height: 44px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #0ea5e9 0%, #10b981 100%);
  color: #f8fafc;
  border: 1px solid rgba(56, 189, 248, 0.34);
  border-radius: 10px;
  font-weight: 900;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(14, 165, 233, 0.22);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const SettlementBreakdown = styled.div`
  background: rgba(2, 6, 23, 0.32);
  border-radius: 12px;
  padding: 14px;
  margin-top: 16px;
  border: 1px solid rgba(20, 184, 166, 0.2);
`;

const BreakdownRow = styled.div<{ total?: boolean }>`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: ${props => props.total ? 'none' : '1px solid rgba(148, 163, 184, 0.1)'};
  font-weight: ${props => props.total ? '700' : '400'};
  font-size: ${props => props.total ? '16px' : '14px'};
  color: ${props => props.total ? '#34d399' : '#e2e8f0'};
`;

const FixedIncomeWorkspace = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const AnalyticsExpanderHeader = styled(ExpanderHeader)`
  margin-top: 0;
`;

const AnalyticsPanelStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const WorkspaceBand = styled.section`
  border: 1px solid rgba(51, 65, 85, 0.74);
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.78) 0%, rgba(8, 13, 23, 0.9) 100%);
  border-radius: 14px;
  padding: 18px;
`;

const BandHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 18px;
  margin-bottom: 18px;

  @media (max-width: 860px) {
    flex-direction: column;
  }
`;

const BandTitleGroup = styled.div`
  min-width: 0;
`;

const BandEyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  font-weight: 800;
  color: #7dd3fc;
  letter-spacing: 0.11em;
  text-transform: uppercase;
  margin-bottom: 6px;
`;

const BandTitle = styled.h3`
  margin: 0;
  color: #f8fafc;
  font-size: 18px;
  line-height: 1.25;
  letter-spacing: 0;
  overflow-wrap: anywhere;
`;

const BandSubline = styled.div`
  margin-top: 6px;
  color: #94a3b8;
  font-size: 13px;
`;

const ScopePills = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const ScopePill = styled.div<{ $tone?: 'buy' | 'sell' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 28px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid ${({ $tone = 'neutral' }) => {
    if ($tone === 'buy') return 'rgba(96, 165, 250, 0.32)';
    if ($tone === 'sell') return 'rgba(245, 158, 11, 0.32)';
    return 'rgba(100, 116, 139, 0.34)';
  }};
  background: ${({ $tone = 'neutral' }) => {
    if ($tone === 'buy') return 'rgba(96, 165, 250, 0.1)';
    if ($tone === 'sell') return 'rgba(245, 158, 11, 0.1)';
    return 'rgba(15, 23, 42, 0.64)';
  }};
  color: ${({ $tone = 'neutral' }) => {
    if ($tone === 'buy') return '#93c5fd';
    if ($tone === 'sell') return '#fbbf24';
    return '#cbd5e1';
  }};
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  white-space: nowrap;
`;

const TradeCockpit = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 0.9fr) minmax(0, 1.1fr);
  gap: 16px;
  align-items: stretch;

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
  }
`;

const InstrumentCard = styled.div`
  border: 1px solid rgba(56, 189, 248, 0.2);
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(8, 47, 73, 0.28) 0%, rgba(15, 23, 42, 0.72) 62%, rgba(20, 83, 45, 0.14) 100%);
  padding: 16px;
  min-width: 0;
`;

const InstrumentIdentity = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

const InstrumentIcon = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(14, 165, 233, 0.12);
  border: 1px solid rgba(14, 165, 233, 0.26);
  color: #7dd3fc;
  flex-shrink: 0;
`;

const InstrumentName = styled.div`
  color: #f8fafc;
  font-size: 17px;
  line-height: 1.25;
  font-weight: 900;
  overflow-wrap: anywhere;
`;

const InstrumentMeta = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 7px;
  color: #94a3b8;
  font-size: 12px;
`;

const InstrumentCode = styled.span`
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  color: #bfdbfe;
  font-weight: 800;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(130px, 1fr));
  gap: 10px;

  @media (max-width: 1180px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const MetricTile = styled.div`
  padding: 12px;
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.66);
  border: 1px solid rgba(71, 85, 105, 0.38);
  min-width: 0;
`;

const MetricLabel = styled.div`
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 5px;
`;

const MetricValue = styled.div<{ $accent?: 'green' | 'blue' | 'amber' }>`
  color: ${({ $accent }) => {
    if ($accent === 'green') return '#34d399';
    if ($accent === 'blue') return '#7dd3fc';
    if ($accent === 'amber') return '#fbbf24';
    return '#f8fafc';
  }};
  font-size: 15px;
  line-height: 1.2;
  font-weight: 900;
  overflow-wrap: anywhere;
`;

const PricingWorkbenchGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 0.42fr) minmax(0, 0.58fr);
  gap: 16px;
  align-items: start;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const PricingInputPanel = styled.div`
  border: 1px solid rgba(71, 85, 105, 0.38);
  border-radius: 12px;
  background: rgba(2, 6, 23, 0.2);
  padding: 14px;
`;

const InfoNote = styled.div<{ $error?: boolean }>`
  padding: 11px 13px;
  border-radius: 10px;
  border: 1px solid ${p => p.$error ? 'rgba(239,68,68,0.36)' : 'rgba(56,189,248,0.26)'};
  background: ${p => p.$error ? 'rgba(127,29,29,0.16)' : 'rgba(56,189,248,0.08)'};
  color: ${p => p.$error ? '#fca5a5' : '#bae6fd'};
  font-size: 12px;
  font-weight: 700;
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const formatCurrencyValue = (value?: number | null, fractionDigits = 0) => {
  if (value == null || !Number.isFinite(value)) return '--';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
};

/**
 * Settle date query for a bond (ADR 0009 §4.2): T+1 on the bond's own calendars (SIFMA for USD),
 * never the book's. The server stores the same date.
 */
const fiSettlementQuery = (tradeDate: string, currency?: string | null) => ({
  tradeDate,
  ticketType: 'PUBLIC_FI',
  convention: 'T+1',
  currency: currency || 'USD',
});

const getDurationMissingFields = (formData: any) => {
  const missing: string[] = [];
  if (!formData.portfolio && !formData.portfolioId) missing.push('portfolio');
  if (!formData.cusip) missing.push('CUSIP');
  if (!formData.amount) missing.push('amount');
  if (!formData.settleDate) missing.push('settle date');
  return missing;
};

const normalizePortfolioKey = (value: any): string => {
  if (value == null) return '';
  return String(value).trim().toLowerCase();
};

const resolveSelectedPortfolio = (formData: any, portfolios: any[] = []) => {
  if (!Array.isArray(portfolios) || portfolios.length === 0) {
    return null;
  }

  const rawPortfolio = typeof formData.portfolio === 'object'
    ? (formData.portfolio?.portfolioName || formData.portfolio?.name || formData.portfolio?.portfolioCode || '')
    : formData.portfolio;
  const target = normalizePortfolioKey(rawPortfolio);
  const portfolioId = formData.portfolioId ?? formData.portfolio?.id;
  if (portfolioId != null && String(portfolioId).trim() !== '') {
    const byId = portfolios.find((p: any) => String(p.id) === String(portfolioId).trim());
    const idMatchesVisibleSelection = !target
      || normalizePortfolioKey(byId?.portfolioName) === target
      || normalizePortfolioKey(byId?.portfolioCode) === target;
    if (byId && idMatchesVisibleSelection) return byId;
  }

  if (!target) return null;

  const byName = portfolios.find((p: any) => normalizePortfolioKey(p.portfolioName) === target);
  if (byName) return byName;

  const byCode = portfolios.find((p: any) => normalizePortfolioKey(p.portfolioCode) === target);
  if (byCode) return byCode;

  if (/^\d+$/.test(String(rawPortfolio).trim())) {
    return portfolios.find((p: any) => String(p.id) === String(rawPortfolio).trim()) || null;
  }

  return null;
};

const inferFixedIncomeSubClass = (security: any): string | null => {
  if (!security) return null;
  const existing = security.assetSubClass || security.assetSubClassName;
  if (existing) {
    return typeof existing === 'string' ? existing : existing.assetSubClassName || null;
  }
  const text = [
    security.securityType,
    security.securityName,
    security.assetClassName,
    typeof security.assetClass === 'string' ? security.assetClass : security.assetClass?.assetClassName,
  ].filter(Boolean).join(' ').toUpperCase();
  if (text.includes('TREASURY') || text.includes('GOVERNMENT')) return 'Treasury Bonds';
  if (text.includes('MUNICIPAL') || text.includes('MUNI')) return 'Municipal Bonds';
  // High yield before the generic corporate branch — HY paper is corporate too,
  // and falling through to the IG bucket would misstate credit quality.
  if (text.includes('HIGH YIELD') || text.includes('HY_BOND')) return 'High Yield Bonds';
  // F-PUB-030: 'Corporate Bonds' is not a row in dim_asset_sub_class, so this
  // filled the field with a value that buildTransactionData could not resolve to
  // an id — the trade then persisted asset_sub_class_id NULL. The seeded bucket
  // is Investment Grade Corporate Bonds (V439).
  if (text.includes('CORPORATE') || text.includes('INVESTMENT GRADE') || text.includes('IG_BOND')) {
    return 'Investment Grade Corporate Bonds';
  }
  if (text.includes('LEVERAGED LOAN')) return 'Leveraged Loans';
  if (text.includes('CLO')) return 'CLO Tranches';
  return null;
};

const isoDate = (value: any): string | null => {
  if (Array.isArray(value) && value.length >= 3) {
    return `${value[0]}-${String(value[1]).padStart(2, '0')}-${String(value[2]).padStart(2, '0')}`;
  }
  return value ? String(value).slice(0, 10) : null;
};

// A BUY or SELL needs a security still outstanding on the trade date. Defaulted debt is the
// exception: claims on it keep trading past maturity. The backend enforces the same rule.
const maturedSecurityMessage = (security: any, tradeDate?: string): string | null => {
  const maturity = isoDate(security?.maturityDate);
  const trade = isoDate(tradeDate);
  if (!maturity || !trade || String(security?.securityStatus || '').toUpperCase() === 'DEFAULTED') {
    return null;
  }
  return maturity < trade
    ? `${security.cusip || 'This security'} matured on ${maturity}. A matured security can't be bought or sold.`
    : null;
};

const FixedIncomeFormEffects: React.FC<{
  formData: any;
  setFormData: any;
  dimensionData: any;
  pendingAssetSubClassRef: React.MutableRefObject<string | null>;
  setDurationImpact: React.Dispatch<React.SetStateAction<any>>;
  setDurationLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setDurationError: React.Dispatch<React.SetStateAction<string | null>>;
  autoSettleRef: React.MutableRefObject<string | null>;
}> = ({
  formData,
  setFormData,
  dimensionData,
  pendingAssetSubClassRef,
  setDurationImpact,
  setDurationLoading,
  setDurationError,
  autoSettleRef,
}) => {
  // ADR 0009 §4.2: while the settle date is still the one this ticket set (a new ticket, or after a
  // trade date change), show the browser's count at once and replace it with the server's answer.
  // Also re-counts when the currency (and so the calendar) changes. A settle date the user typed,
  // or one loaded from a stored trade, is left alone.
  React.useEffect(() => {
    if (!formData.tradeDate || formData.settleDate !== autoSettleRef.current) return undefined;
    const query = fiSettlementQuery(formData.tradeDate, formData.currency);
    const provisional = provisionalSettleDate(query);
    autoSettleRef.current = provisional;
    if (provisional !== formData.settleDate) {
      setFormData((prev: any) => ({ ...prev, settleDate: provisional }));
    }
    let current = true;
    resolveSettleDate(query).then(settleDate => {
      if (!current || !settleDate) return;
      setFormData((prev: any) => {
        // Only if the form still holds what was asked, and nobody typed over the automatic date.
        if (prev.tradeDate !== query.tradeDate || (prev.currency || 'USD') !== query.currency
            || prev.settleDate !== autoSettleRef.current) {
          return prev;
        }
        autoSettleRef.current = settleDate;
        return { ...prev, settleDate };
      });
    });
    return () => { current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.tradeDate, formData.currency]);

  React.useEffect(() => {
    if (formData.cusip && !formData.assetSubClass) {
      const inferredSubClass = pendingAssetSubClassRef.current || inferFixedIncomeSubClass({
        securityName: formData.security,
        securityType: formData.security,
        assetClassName: formData.assetClass,
      });

      if (inferredSubClass) {
        setFormData((prev: any) => (
          prev.assetSubClass ? prev : { ...prev, assetSubClass: inferredSubClass }
        ));
        pendingAssetSubClassRef.current = null;
      }
    }
  }, [
    formData.assetClass,
    formData.assetSubClass,
    formData.cusip,
    formData.security,
    pendingAssetSubClassRef,
    setFormData,
  ]);

  const prevDurationKeyRef = React.useRef<string>('');

  React.useEffect(() => {
    if ((formData.portfolio || formData.portfolioId) && formData.cusip && formData.amount && formData.settleDate) {
      const currentKey = `${formData.portfolioId || formData.portfolio}-${formData.cusip}-${formData.transactionType}-${formData.amount}-${formData.price || 'TRACE'}-${formData.settleDate}`;

      if (prevDurationKeyRef.current === currentKey) {
        return;
      }

      prevDurationKeyRef.current = currentKey;
      setDurationImpact(null);
      setDurationError(null);
      setDurationLoading(false);
    } else {
      prevDurationKeyRef.current = '';
      setDurationImpact(null);
      setDurationError(null);
      setDurationLoading(false);
    }
  }, [
    formData.portfolio,
    formData.portfolioId,
    formData.cusip,
    formData.transactionType,
    formData.amount,
    formData.price,
    formData.settleDate,
    setDurationImpact,
    setDurationError,
    setDurationLoading,
  ]);

  return null;
};

const FixedIncomeTicketScreen: React.FC = () => {
  const [cusipSuggestions, setCusipSuggestions] = useState<any[]>([]);
  // Why the trade date field is empty although a date was typed (a weekend or holiday), with the next business day.
  const [tradeDateProblem, setTradeDateProblem] = useState<{ problem: string; fix: { iso: string; label: string } | null } | null>(null);
  const [showCusipSuggestions, setShowCusipSuggestions] = useState(false);
  const { isRetail } = useTenantConfig();
  const { user: authUser } = useAuth();
  const [durationImpact, setDurationImpact] = useState<any>(null);
  const [durationLoading, setDurationLoading] = useState(false);
  const [durationError, setDurationError] = useState<string | null>(null);

  // IBOR Pricing State
  const [pricingMode, setPricingMode] = useState<'price' | 'yield'>('price');
  const [yieldValue, setYieldValue] = useState<string>('');
  const [pricingData, setPricingData] = useState<any>(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [analyticsExpanded, setAnalyticsExpanded] = useState(false);

  // Check for pending trade data from Discovery or other sources
  const getPendingTradeData = () => {
    try {
      const pendingData = sessionStorage.getItem('pendingTradeData');
      if (pendingData) {
        const parsed = JSON.parse(pendingData);
        // Clear the sessionStorage after reading
        sessionStorage.removeItem('pendingTradeData');
        return parsed;
      }
    } catch (error) {
      console.error('Error reading pending trade data:', error);
    }
    return null;
  };

  const pendingTrade = getPendingTradeData();
  const initialCurrency = pendingTrade?.currency || 'USD';
  const initialTradeDate = pendingTrade?.tradeDate || getTodayDate(calendarsFor('PUBLIC_FI', initialCurrency));
  const initialSettleDate = pendingTrade?.settleDate
    || provisionalSettleDate(fiSettlementQuery(initialTradeDate, initialCurrency));
  // The settle date this ticket last set by itself; null once it came from elsewhere (a hand-off,
  // a stored trade). Only an automatic date is re-counted when the currency changes.
  const autoSettleRef = React.useRef<string | null>(pendingTrade?.settleDate ? null : initialSettleDate);

  const initialFormData = {
    transactionType: (pendingTrade?.transactionType || 'BUY') as 'BUY' | 'SELL',
    assetManager: pendingTrade?.assetManager || '',
    portfolioManager: pendingTrade?.portfolioManager || '',
    assetClass: 'Fixed Income',
    assetSubClass: pendingTrade?.assetSubClassName || '',
    security: pendingTrade?.securityName || pendingTrade?.security || '',
    cusip: pendingTrade?.cusip || '',
    price: pendingTrade?.price?.toString() || '',
    portfolioId: pendingTrade?.portfolioId || pendingTrade?.portfolio?.id || '',
    portfolio: pendingTrade?.portfolioName
      || pendingTrade?.portfolio?.portfolioName
      || (typeof pendingTrade?.portfolio === 'string' ? pendingTrade.portfolio : ''),
    amount: pendingTrade?.amount?.toString() || pendingTrade?.notional?.toString() || '',
    tradeDate: initialTradeDate,
    settleDate: initialSettleDate,
    submittedBy: pendingTrade?.submittedBy || authUser?.fullName || '',
    currency: initialCurrency,
    executedBy: pendingTrade?.executedBy || '',
    notes: pendingTrade?.notes || '',
    rationale: pendingTrade?.rationale || '',
    // Execution venue fields (populated when executed via FIX)
    executionBroker: pendingTrade?.executionBroker || '',
    ecnPlatform: pendingTrade?.ecnPlatform || '',
    executionVenue: pendingTrade?.executionVenue || '',
    executionId: pendingTrade?.executionId || '',
    executionTime: pendingTrade?.executionTime || ''
  };
  
  // Fetch bond pricing from IBOR API
  const fetchBondPricing = useCallback(async (cusip: string, price: string | null, yld: string | null, settleDate: string, amount: string) => {
    if (!cusip) return;
    
    setPricingLoading(true);
    try {
      const parAmount = parseAmountShorthand(amount) || 1000000;
      const response = await axios.post('/api/ibor/bond-pricing', {
        cusip,
        price: pricingMode === 'price' && price ? parseFloat(price) : null,
        yield: pricingMode === 'yield' && yld ? parseFloat(yld) : null,
        settlementDate: settleDate,
        parAmount
      });
      
      setPricingData(response.data);
      
      // Auto-populate the calculated value
      if (response.data && !response.data.error) {
        if (pricingMode === 'yield' && response.data.cleanPrice) {
          // If user entered yield, update the price field
          return response.data.cleanPrice;
        } else if (pricingMode === 'price' && response.data.yieldToMaturity) {
          // If user entered price, update the yield field
          setYieldValue(response.data.yieldToMaturity.toFixed(4));
        }
      }
    } catch (error: any) {
      console.error('Error fetching bond pricing:', error);
      setPricingData({ error: error.response?.data?.error || 'Failed to calculate pricing' });
    } finally {
      setPricingLoading(false);
    }
    return null;
  }, [pricingMode]);
  
  const fetchCusipSuggestions = async (prefix: string) => {
    if (!prefix || prefix.length < 1) {
      setCusipSuggestions([]);
      return;
    }
    
    try {
      const response = await axios.get(`/api/dimensions/securities/cusip/search?prefix=${encodeURIComponent(prefix)}`);
      setCusipSuggestions(response.data || []);
      if (response.data && response.data.length > 0) {
        setShowCusipSuggestions(true);
      }
    } catch (error: any) {
      console.error('Error fetching CUSIP suggestions:', error);
      setCusipSuggestions([]);
      setShowCusipSuggestions(false);
    }
  };
  
  const handleCusipSelect = async (security: any, setFormData: any, setErrors: any, errors: any, dimensionData: any, tradeDate?: string) => {
    setFormData((prev: any) => ({
      ...prev,
      cusip: security.cusip,
      security: security.securityName || security.cusip
    }));
    setShowCusipSuggestions(false);
    setCusipSuggestions([]);
    
    // Clear CUSIP error if any
    if (errors.cusip) {
      setErrors((prev: any) => {
        const newErrors = { ...prev };
        delete newErrors.cusip;
        return newErrors;
      });
    }
    
    // Fetch full security details to get complete asset class/subclass info
    try {
      const fullSecurityResponse = await axios.get(`/api/dimensions/securities/cusip/${security.cusip}`);
      const fullSecurity = fullSecurityResponse.data;
      const matured = maturedSecurityMessage(fullSecurity, tradeDate);
      if (matured) {
        setErrors((prev: any) => ({ ...prev, cusip: matured }));
      }
      
      // Auto-populate currency if available
      if (fullSecurity.currency) {
        setFormData((prev: any) => ({ ...prev, currency: fullSecurity.currency }));
      }
      
      // Auto-populate asset class and sub-class if available
      if (fullSecurity.assetClass) {
        const assetClassName = typeof fullSecurity.assetClass === 'string' 
          ? fullSecurity.assetClass 
          : fullSecurity.assetClass.assetClassName;
        setFormData((prev: any) => ({ ...prev, assetClass: assetClassName }));
        
        pendingAssetSubClassRef.current = inferFixedIncomeSubClass(fullSecurity);
      } else if (security.assetClass) {
        // Fallback to suggestion data
        const assetClassName = typeof security.assetClass === 'string' 
          ? security.assetClass 
          : security.assetClass.assetClassName;
        setFormData((prev: any) => ({ ...prev, assetClass: assetClassName }));
        
        pendingAssetSubClassRef.current = inferFixedIncomeSubClass(security);
      } else {
        pendingAssetSubClassRef.current = inferFixedIncomeSubClass(fullSecurity) || inferFixedIncomeSubClass(security);
      }
    } catch (error) {
      console.error('Error fetching full security details:', error);
      // Fallback to suggestion data
      if (security.currency) {
        setFormData((prev: any) => ({ ...prev, currency: security.currency }));
      }
      if (security.assetClass) {
        const assetClassName = typeof security.assetClass === 'string' 
          ? security.assetClass 
          : security.assetClass.assetClassName;
        setFormData((prev: any) => ({ ...prev, assetClass: assetClassName }));
      }
      pendingAssetSubClassRef.current = inferFixedIncomeSubClass(security);
    }
  };

  const resolveCusipFromSmf = async (cusip: string, setFormData: any, setErrors?: any, tradeDate?: string) => {
    const normalizedCusip = cusip.trim().toUpperCase();
    if (normalizedCusip.length < 6) {
      return;
    }

    try {
      const fullSecurityResponse = await axios.get(`/api/dimensions/securities/cusip/${normalizedCusip}`);
      const fullSecurity = fullSecurityResponse.data;
      if (!fullSecurity) {
        return;
      }
      const matured = maturedSecurityMessage(fullSecurity, tradeDate);
      if (matured && setErrors) {
        setErrors((prev: any) => ({ ...prev, cusip: matured }));
      }

      const assetClassName = fullSecurity.assetClass
        ? (typeof fullSecurity.assetClass === 'string'
          ? fullSecurity.assetClass
          : fullSecurity.assetClass.assetClassName)
        : (fullSecurity.assetClassName || 'Fixed Income');
      const assetSubClassName = inferFixedIncomeSubClass(fullSecurity);

      setFormData((prev: any) => ({
        ...prev,
        cusip: fullSecurity.cusip || normalizedCusip,
        security: fullSecurity.securityName || prev.security,
        assetClass: assetClassName || prev.assetClass || 'Fixed Income',
        assetSubClass: assetSubClassName || prev.assetSubClass,
        currency: fullSecurity.currency || prev.currency || 'USD',
      }));
    } catch (error) {
      console.error('Error resolving CUSIP from SMF:', error);
    }
  };

  const validateForm = (formData: any, action: string, useAllocations: boolean = false): { [key: string]: string } => {
    const errors: { [key: string]: string } = {};

    // Field-level validation helper
    const validateField = (fieldName: string, value: string): string => {
      switch (fieldName) {
        case 'assetManager':
          if (!value?.trim()) return 'Asset Manager is required';
          break;
        case 'portfolioManager':
          if (!value?.trim()) return 'Portfolio Manager is required';
          break;
        case 'assetClass':
          if (!value?.trim()) return 'Asset Class is required';
          break;
        case 'assetSubClass':
          if (!value?.trim()) return 'Asset Sub Class is required';
          break;
        case 'security':
          if (!value?.trim()) return 'Security name is required';
          break;
        case 'cusip':
          if (!value?.trim()) return 'CUSIP is required';
          if (value.trim().length < 6) return 'CUSIP must be at least 6 characters';
          break;
        case 'portfolio':
          // Portfolio is only required if not using allocations
          if (!useAllocations && !value?.trim()) return 'Portfolio is required';
          break;
        case 'amount':
          if (!value || value.trim() === '') return 'Amount is required';
          const amountNum = parseAmountShorthand(value);
          if (isNaN(amountNum) || amountNum <= 0) return 'Amount must be greater than 0';
          break;
        case 'price':
          if (action === 'execute' && (!value || value.trim() === '')) {
            return 'Price is required for execution';
          }
          if (value && value.trim() !== '') {
            const priceNum = parseFloat(value);
            if (isNaN(priceNum) || priceNum <= 0) return 'Price must be greater than 0';
          }
          break;
        case 'executedBy':
          if (action === 'execute' && !value?.trim()) {
            return 'Executed By is required for execution';
          }
          break;
        case 'tradeDate':
          // A typed weekend or holiday leaves the field empty on purpose; say that, not "required".
          if (!value) return tradeDateProblem ? `${tradeDateProblem.problem}${tradeDateProblem.fix ? ` — use ${tradeDateProblem.fix.label}` : ''}` : 'Trade Date is required';
          const tradeDate = new Date(value);
          if (isNaN(tradeDate.getTime())) return 'Invalid trade date';
          break;
        case 'settleDate':
          if (!value) return 'Settle Date is required';
          const settleDate = new Date(value);
          if (isNaN(settleDate.getTime())) return 'Invalid settle date';
          if (formData.tradeDate) {
            const tradeDate = new Date(formData.tradeDate);
            if (settleDate < tradeDate) return 'Settle date cannot be before trade date';
          }
          break;
      }
      return '';
    };

    // Price and Executed By are only required when executing
    if (action === 'execute') {
      const priceError = validateField('price', formData.price || '');
      if (priceError) errors.price = priceError;
      
      const executedByError = validateField('executedBy', formData.executedBy || '');
      if (executedByError) errors.executedBy = executedByError;
    }

    // Other fields are required for draft and send
    if (action !== 'execute') {
      // Retail clients don't need Asset Manager (RIA is the only manager)
      const fieldsToValidate = isRetail
        ? ['portfolioManager', 'assetClass', 'assetSubClass', 'security', 'cusip', 'amount', 'tradeDate', 'settleDate']
        : ['assetManager', 'portfolioManager', 'assetClass', 'assetSubClass', 'security', 'cusip', 'amount', 'tradeDate', 'settleDate'];

      // Only add portfolio to validation if not using allocations
      if (!useAllocations) {
        fieldsToValidate.push('portfolio');
      }
      
      fieldsToValidate.forEach(field => {
        const error = validateField(field, formData[field] || '');
        if (error) {
          errors[field] = error;
        }
      });
    }

    return errors;
  };

  const buildTransactionData = async (formData: any, dimensionData: any, useAllocations: boolean = false): Promise<any> => {
    // Validate required fields
    if (!formData.cusip || !formData.cusip.trim()) {
      throw new Error('CUSIP is required');
    }
    if (!formData.amount || !formData.amount.trim()) {
      throw new Error('Amount is required');
    }
    
    // Look up security by CUSIP
    let securityId: number | null = null;
    let maturedMessage: string | null = null;
    try {
      const securityResponse = await axios.get(`/api/dimensions/securities/cusip/${formData.cusip.trim()}`);
      maturedMessage = maturedSecurityMessage(securityResponse.data, formData.tradeDate);
      if (securityResponse.data && securityResponse.data.id) {
        securityId = securityResponse.data.id;
      } else {
        throw new Error(`Security with CUSIP ${formData.cusip} not found or missing ID`);
      }
    } catch (error: any) {
      const errorMsg = error.response?.status === 404 
        ? `Security with CUSIP ${formData.cusip} not found. Please verify the CUSIP is correct.`
        : `Error looking up security: ${error.message || error.response?.data?.error || 'Unknown error'}`;
      throw new Error(errorMsg);
    }

    if (!securityId) {
      throw new Error(`Security ID is null for CUSIP ${formData.cusip}`);
    }
    if (maturedMessage) {
      throw new Error(maturedMessage);
    }

    // Look up portfolio only if not using allocations
    let portfolioId: number | null = null;
    if (!useAllocations) {
      if (!formData.portfolio && !formData.portfolioId) {
        throw new Error('Portfolio is required');
      }
      const portfolio = resolveSelectedPortfolio(formData, dimensionData.portfolios);
      if (!portfolio || !portfolio.id) {
        throw new Error(`Portfolio "${formData.portfolio}" not found`);
      }
      portfolioId = portfolio.id;
    }

    const amount = parseAmountShorthand(formData.amount);
    if (!amount || amount <= 0 || isNaN(amount)) {
      throw new Error('Invalid amount. Please enter a valid amount (e.g., 1M, 2MM, 1000000)');
    }

    // Look up portfolio manager ID
    let portfolioManagerId: number | null = null;
    if (formData.portfolioManager && formData.portfolioManager.trim()) {
      const pm = dimensionData.portfolioManagers.find((pm: any) => pm.portfolioManagerName === formData.portfolioManager.trim());
      if (pm && pm.id) {
        portfolioManagerId = pm.id;
      }
    }

    // Look up asset manager ID
    let assetManagerId: number | null = null;
    if (formData.assetManager && formData.assetManager.trim()) {
      const am = dimensionData.assetManagers.find((am: any) => am.assetManagerName === formData.assetManager.trim());
      if (am && am.id) {
        assetManagerId = am.id;
      }
    }

    // Look up asset sub class ID
    let assetSubClassId: number | null = null;
    if (formData.assetSubClass && formData.assetSubClass.trim()) {
      const asc = dimensionData.assetSubClasses.find((asc: any) => asc.assetSubClassName === formData.assetSubClass.trim());
      if (asc && asc.id) {
        assetSubClassId = asc.id;
      }
    }

    return {
      transactionType: formData.transactionType,
      securityId: securityId,
      portfolioId: portfolioId,
      assetManagerId: assetManagerId,
      portfolioManagerId: portfolioManagerId,
      assetSubClassId: assetSubClassId,
      amount: amount,
      tradeDate: formData.tradeDate,
      settleDate: formData.settleDate,
      price: formData.price ? parseFloat(formData.price) : null,
      // For fixed income: quantity = par amount (not amount/price)
      quantity: amount,
      // Include calculated values from Bond Pricing Calculator
      accruedInterest: pricingData?.accruedInterest || 0,
      principal: pricingData?.principalAmount || null,
      netAmount: pricingData?.settlementAmount || null,
      currentFace: pricingData?.parAmount || null,
      currency: formData.currency || 'USD',
      ticketType: 'PUBLIC_FI',
      eventType: formData.transactionType === 'SELL' ? 'DISPOSE' : 'ACQUIRE',
      notes: formData.notes || '',
      rationale: formData.rationale || '',
      createdBy: formData.submittedBy,
      executedBy: formData.executedBy || null,
      status: 'DRAFT'
    };
  };

  const loadTransactionData = (transaction: any) => {
    const currency = transaction.currency || 'USD';
    const tradeDate = transaction.tradeDate || getTodayDate(calendarsFor('PUBLIC_FI', currency));
    // A stored settle date is the server's and is kept; only a missing one is counted here.
    autoSettleRef.current = transaction.settleDate
      ? null
      : provisionalSettleDate(fiSettlementQuery(tradeDate, currency));
    return {
      transactionType: transaction.transactionType,
      assetManager: transaction.assetManagerName || transaction.assetManager || '',
      portfolioManager: transaction.portfolioManagerName || transaction.portfolioManager || '',
      assetClass: transaction.assetClassName || transaction.assetClass || 'Fixed Income',
      assetSubClass: transaction.assetSubClassName || transaction.assetSubClass || '',
      security: transaction.securityName || transaction.security || '',
      cusip: transaction.cusip || '',
      price: transaction.price ? transaction.price.toString() : '',
      portfolioId: transaction.portfolio?.id || transaction.portfolioId || '',
      portfolio: transaction.portfolioName
        || transaction.portfolio?.portfolioName
        || (typeof transaction.portfolio === 'string' ? transaction.portfolio : ''),
      amount: transaction.amount ? transaction.amount.toString() : '',
      tradeDate,
      settleDate: transaction.settleDate || autoSettleRef.current,
      submittedBy: transaction.createdBy || authUser?.fullName || '',
      currency,
      executedBy: transaction.executedBy || '',
      notes: transaction.notes || '',
      rationale: transaction.rationale || '',
      // Execution venue fields (populated when executed via FIX)
      executionBroker: transaction.executionBroker || '',
      ecnPlatform: transaction.ecnPlatform || '',
      executionVenue: transaction.executionVenue || '',
      executionId: transaction.executionId || '',
      executionTime: transaction.executionTime || ''
    };
  };

  // Auto-set asset sub-class once sub-classes are loaded
  const pendingAssetSubClassRef = React.useRef<string | null>(null);
  
    // Fetch duration impact when relevant fields change
  const fetchDurationImpact = useCallback(async (formData: any, dimensionData: any) => {
    // Need: portfolio, security (CUSIP), transactionType, amount, price (optional), settleDate
    if ((!formData.portfolio && !formData.portfolioId) || !formData.cusip || !formData.amount || !formData.settleDate) {
      setDurationImpact(null);
      setDurationLoading(false);
      setDurationError(null);
      return;
    }
    
    const portfolio = resolveSelectedPortfolio(formData, dimensionData.portfolios || []);
    
    if (!portfolio) {
      setDurationImpact(null);
      setDurationLoading(false);
      setDurationError('Portfolio not found');
      return;
    }
    
    const amount = parseAmountShorthand(formData.amount);
    
    if (amount <= 0 || isNaN(amount)) {
      setDurationImpact(null);
      setDurationLoading(false);
      setDurationError('Invalid amount');
      return;
    }
    
    setDurationLoading(true);
    setDurationError(null);
    
    try {
      const params: any = {
        portfolioId: portfolio.id,
        cusip: formData.cusip,
        transactionType: formData.transactionType || 'BUY',
        amount: amount,
        settleDate: formData.settleDate
      };
      
      // Only include price if provided and valid
      if (formData.price) {
        const price = parseFloat(formData.price);
        if (price > 0 && !isNaN(price)) {
          params.price = price;
        }
      }
      
      const response = await axios.get('/api/analytics/portfolio-impact/duration', {
        params: params
      });
      
      setDurationImpact({
        ...response.data,
        portfolioId: portfolio.id,
        portfolioName: portfolio.portfolioName,
      });
      setDurationError(null);
    } catch (error: any) {
      console.error('Error fetching duration impact:', error);
      if (error.response?.data?.error) {
        setDurationError(error.response.data.error);
      } else {
        setDurationError('Failed to calculate duration impact. Please check that the portfolio has positions and the security has valid analytics data.');
      }
      setDurationImpact(null);
    } finally {
      setDurationLoading(false);
    }
  }, []);
  
  return (
    <BaseTicketScreen
      title="Fixed Income Trade Entry"
      assetClass="Fixed Income"
      hidePortfolioField={true}
      initialFormData={initialFormData}
      validateForm={validateForm}
      buildTransactionData={buildTransactionData}
      loadTransactionData={loadTransactionData}
      suppressExecutionClearingSection
    >
      {({ formData, setFormData, errors, setErrors, dimensionData, transactionStatus, isFieldDisabled, handleInputChange, handleInputBlur, useAllocations, editingTransactionId, households, selectedHousehold, setSelectedHousehold, filteredPortfolios }) => {
        const parsedAmount = parseAmountShorthand(formData.amount);
        const cleanPrice = formData.price ? parseFloat(formData.price) : null;
        const principalPreview = parsedAmount > 0 && cleanPrice && cleanPrice > 0
          ? parsedAmount * cleanPrice / 100
          : null;
        const settlementPreview = pricingData?.settlementAmount ?? principalPreview;
        const yieldPreview = pricingData?.yieldToMaturity ?? (yieldValue ? parseFloat(yieldValue) : null);
        const sideTone = formData.transactionType === 'SELL' ? 'sell' : 'buy';
        const displayPortfolio = useAllocations ? 'Allocation Grid' : (formData.portfolio || 'Select Portfolio');
        const displaySecurity = formData.security || 'Select a fixed income instrument';
        const displayCusip = formData.cusip || 'CUSIP pending';
        const missingDurationFields = getDurationMissingFields(formData);
        const canCalculateDuration = missingDurationFields.length === 0;
        const selectPortfolio = (portfolioName: string) => {
          const selectedPortfolio = resolveSelectedPortfolio(
            { portfolio: portfolioName },
            dimensionData.portfolios || []
          );
          setFormData((prev: any) => ({
            ...prev,
            portfolio: portfolioName,
            portfolioId: selectedPortfolio?.id || '',
          }));
          if (errors.portfolio) {
            setErrors((prev: any) => {
              const next = { ...prev };
              delete next.portfolio;
              return next;
            });
          }
        };

        return (
        <FixedIncomeWorkspace>
          <FixedIncomeFormEffects
            formData={formData}
            setFormData={setFormData}
            dimensionData={dimensionData}
            pendingAssetSubClassRef={pendingAssetSubClassRef}
            setDurationImpact={setDurationImpact}
            setDurationLoading={setDurationLoading}
            setDurationError={setDurationError}
            autoSettleRef={autoSettleRef}
          />

          <WorkspaceBand>
            <BandHeader>
              <BandTitleGroup>
                <BandEyebrow><Landmark size={14} /> Fixed Income Ticket</BandEyebrow>
                <BandTitle>{displaySecurity}</BandTitle>
                <BandSubline>
                  <InstrumentCode>{displayCusip}</InstrumentCode> | {formData.assetSubClass || 'Sub-class pending'} | {formData.currency || 'USD'}
                </BandSubline>
              </BandTitleGroup>
              <ScopePills>
                <ScopePill $tone={sideTone}>
                  <ArrowRightLeft size={13} /> {formData.transactionType || 'BUY'}
                </ScopePill>
                <ScopePill>
                  <Database size={13} /> IBOR Draft
                </ScopePill>
                <ScopePill>
                  <ShieldCheck size={13} /> Pre-clear Ready
                </ScopePill>
              </ScopePills>
            </BandHeader>

            <TradeCockpit>
              <InstrumentCard>
                <InstrumentIdentity>
                  <InstrumentIcon><Landmark size={20} /></InstrumentIcon>
                  <div>
                    <InstrumentName>{displaySecurity}</InstrumentName>
                    <InstrumentMeta>
                      <InstrumentCode>{displayCusip}</InstrumentCode>
                      <span>{formData.tradeDate || '--'} trade</span>
                      <span>{formData.settleDate || '--'} settle</span>
                    </InstrumentMeta>
                  </div>
                </InstrumentIdentity>
              </InstrumentCard>

              <MetricsGrid>
                <MetricTile>
                  <MetricLabel>Par Amount</MetricLabel>
                  <MetricValue $accent="blue">{parsedAmount > 0 ? formatCurrencyValue(parsedAmount) : '--'}</MetricValue>
                </MetricTile>
                <MetricTile>
                  <MetricLabel>Clean Price</MetricLabel>
                  <MetricValue>{cleanPrice && cleanPrice > 0 ? cleanPrice.toFixed(4) : '--'}</MetricValue>
                </MetricTile>
                <MetricTile>
                  <MetricLabel>Yield</MetricLabel>
                  <MetricValue $accent="green">{yieldPreview && Number.isFinite(yieldPreview) ? `${yieldPreview.toFixed(4)}%` : '--'}</MetricValue>
                </MetricTile>
                <MetricTile>
                  <MetricLabel>{formData.transactionType === 'SELL' ? 'Est. Proceeds' : 'Est. Settlement'}</MetricLabel>
                  <MetricValue $accent="amber">{formatCurrencyValue(settlementPreview, 2)}</MetricValue>
                </MetricTile>
              </MetricsGrid>
            </TradeCockpit>

            <BandSubline style={{ marginTop: 14 }}>
              Portfolio: <strong style={{ color: '#e2e8f0' }}>{displayPortfolio}</strong>
            </BandSubline>
          </WorkspaceBand>

          {/* SECTION: Trade Details */}
          <WorkspaceBand>
          <BandHeader>
            <BandTitleGroup>
              <BandEyebrow><Activity size={14} /> Order Setup</BandEyebrow>
              <BandTitle>Trade details and participants</BandTitle>
              <BandSubline>Core economics, dates, PM ownership and execution context.</BandSubline>
            </BandTitleGroup>
          </BandHeader>
          <TicketSectionTitle>Trade Details</TicketSectionTitle>
          <FormRow>
            <FormGroup>
              <Label htmlFor="transactionType">Transaction Type*</Label>
              <Select
                id="transactionType"
                value={formData.transactionType}
                onChange={(e) => handleInputChange('transactionType', e.target.value)}
                disabled={isFieldDisabled('transactionType')}
              >
                <option value="BUY">Buy</option>
                <option value="SELL">Sell</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="tradeDate">Trade Date*</Label>
              <DateField
                id="tradeDate"
                value={formData.tradeDate ?? ''}
                onProblem={(problem, fix) => setTradeDateProblem(problem ? { problem, fix } : null)}
                className={errors.tradeDate ? 'error' : ''}
                disabled={isFieldDisabled('tradeDate')}
                calendars={calendarsFor('PUBLIC_FI', formData.currency)}
                onChange={iso => {
                handleInputChange('tradeDate', iso);
                // Settle date T+1 on the bond's calendars; the server's answer replaces it (FixedIncomeFormEffects)
                if (iso) {
                const settleDate = provisionalSettleDate(fiSettlementQuery(iso, formData.currency));
                autoSettleRef.current = settleDate;
                setFormData((prev: any) => ({ ...prev, settleDate }));
                }
                handleInputBlur('tradeDate', iso);
                }}
              />
              {errors.tradeDate && <FieldError>{errors.tradeDate}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="settleDate">Settle Date*</Label>
              <DateField
                id="settleDate"
                value={formData.settleDate ?? ''}
                className={errors.settleDate ? 'error' : ''}
                disabled={isFieldDisabled('settleDate')}
                calendars={settlementCalendars({ ticketType: 'PUBLIC_FI', currency: formData.currency })}
                onChange={iso => handleInputChange('settleDate', iso)}
              />
              {errors.settleDate && <FieldError>{errors.settleDate}</FieldError>}
            </FormGroup>
          </FormRow>

          {/* SECTION: Participants */}
          <TicketSectionTitle>Participants</TicketSectionTitle>
          <FormRow>
            {/* Asset Manager - Hide for retail clients */}
            {!isRetail && (
              <FormGroup>
                <Label htmlFor="assetManager">Asset Manager*</Label>
                {isFieldDisabled('assetManager') && formData.assetManager ? (
                  <Input type="text" value={formData.assetManager} disabled />
                ) : (
                  <Select
                    id="assetManager"
                    value={formData.assetManager}
                    onChange={(e) => handleInputChange('assetManager', e.target.value)}
                    className={errors.assetManager ? 'error' : ''}
                    disabled={isFieldDisabled('assetManager')}
                  >
                    <option value="">Select Asset Manager</option>
                    {dimensionData.assetManagers.map((am: any) => (
                      <option key={am.id} value={am.assetManagerName}>
                        {am.assetManagerName}
                      </option>
                    ))}
                  </Select>
                )}
                {errors.assetManager && <FieldError>{errors.assetManager}</FieldError>}
              </FormGroup>
            )}

            <FormGroup>
              <Label htmlFor="portfolioManager">{isRetail ? 'Advisor*' : 'Portfolio Manager*'}</Label>
              <Select
                id="portfolioManager"
                value={formData.portfolioManager}
                onChange={(e) => handleInputChange('portfolioManager', e.target.value)}
                onBlur={(e) => handleInputBlur('portfolioManager', e.target.value)}
                className={errors.portfolioManager ? 'error' : ''}
                disabled={isFieldDisabled('portfolioManager')}
              >
                <option value="">{isRetail ? 'Select Advisor' : 'Select Portfolio Manager'}</option>
                {dimensionData.portfolioManagers.map((pm: any) => (
                  <option key={pm.id} value={pm.portfolioManagerName}>
                    {pm.portfolioManagerName}
                  </option>
                ))}
              </Select>
              {errors.portfolioManager && <FieldError>{errors.portfolioManager}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="executedBy">Executed By{transactionStatus === 'SENT' ? '*' : ''}</Label>
              {(transactionStatus === 'EXECUTED' || transactionStatus === 'CANCELLED') && formData.executedBy ? (
                <Input type="text" value={formData.executedBy} disabled />
              ) : (
                <Select
                  id="executedBy"
                  value={formData.executedBy}
                  onChange={(e) => handleInputChange('executedBy', e.target.value)}
                  onBlur={(e) => handleInputBlur('executedBy', e.target.value)}
                  disabled={transactionStatus === 'EXECUTED' || transactionStatus === 'CANCELLED'}
                  className={errors.executedBy ? 'error' : ''}
                >
                  <option value="">Select</option>
                  {dimensionData.users && dimensionData.users.length > 0 ? (
                    dimensionData.users.map((user: any) => (
                      <option key={user.id} value={user.fullName}>
                        {user.fullName} {user.role ? `(${user.role})` : ''}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>Loading users...</option>
                  )}
                </Select>
              )}
              {errors.executedBy && <FieldError>{errors.executedBy}</FieldError>}
            </FormGroup>
          </FormRow>

          <ExecutionClearingSection
            formData={formData}
            errors={errors}
            isFieldDisabled={isFieldDisabled}
            handleInputChange={handleInputChange}
            config={getTicketExecutionClearingConfig('Fixed Income', formData.assetSubClass)}
          />

          {/* SECTION: Security Information */}
          <TicketSectionTitle>Security Information</TicketSectionTitle>
          <FormRow>
            <FormGroup>
              <Label htmlFor="cusip">CUSIP*</Label>
              <AutocompleteWrapper>
                <Input
                  id="cusip"
                  type="text"
                  value={formData.cusip}
                  onChange={(e) => {
                    handleInputChange('cusip', e.target.value);
                    if (e.target.value.trim().length >= 1) {
                      fetchCusipSuggestions(e.target.value.trim());
                    } else {
                      setCusipSuggestions([]);
                      setShowCusipSuggestions(false);
                    }
                  }}
                  onBlur={(e) => {
                    handleInputBlur('cusip', e.target.value);
                    resolveCusipFromSmf(e.target.value, setFormData, setErrors, formData.tradeDate);
                    // Delay hiding suggestions to allow click
                    setTimeout(() => setShowCusipSuggestions(false), 200);
                  }}
                  onFocus={() => {
                    if (formData.cusip && formData.cusip.trim().length >= 1) {
                      fetchCusipSuggestions(formData.cusip.trim());
                      setShowCusipSuggestions(true);
                    }
                  }}
                  placeholder="Start typing CUSIP (e.g., 9...)"
                  className={errors.cusip ? 'error' : ''}
                  disabled={isFieldDisabled('cusip')}
                  autoComplete="off"
                />
                {showCusipSuggestions && cusipSuggestions.length > 0 && (
                  <AutocompleteDropdown>
                    {cusipSuggestions.map((security) => (
                      <SuggestionItem
                        key={security.id}
                        onClick={() => handleCusipSelect(security, setFormData, setErrors, errors, dimensionData, formData.tradeDate)}
                        onMouseDown={(e) => e.preventDefault()} // Prevent blur before click
                      >
                        <SuggestionCusip>{security.cusip}</SuggestionCusip>
                        <SuggestionSecurity>{security.securityName || 'No name'}</SuggestionSecurity>
                      </SuggestionItem>
                    ))}
                  </AutocompleteDropdown>
                )}
              </AutocompleteWrapper>
              {errors.cusip && <FieldError>{errors.cusip}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="security">Security*</Label>
              <Input
                id="security"
                type="text"
                value={formData.security}
                onChange={(e) => handleInputChange('security', e.target.value)}
                onBlur={(e) => handleInputBlur('security', e.target.value)}
                placeholder="Enter security name"
                className={errors.security ? 'error' : ''}
                disabled={isFieldDisabled('security')}
              />
              {errors.security && <FieldError>{errors.security}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="assetClass">Asset Class*</Label>
              <Select
                id="assetClass"
                value={formData.assetClass}
                onChange={(e) => {
                  handleInputChange('assetClass', e.target.value);
                  handleInputChange('assetSubClass', ''); // Reset sub-class when class changes
                }}
                className={errors.assetClass ? 'error' : ''}
                disabled={isFieldDisabled('assetClass') || (!!formData.cusip && !!formData.assetClass)}
              >
                <option value="">Select</option>
                {dimensionData.assetClasses.map((ac: any) => (
                  <option key={ac.id} value={ac.assetClassName}>
                    {ac.assetClassName}
                  </option>
                ))}
              </Select>
              {errors.assetClass && <FieldError>{errors.assetClass}</FieldError>}
            </FormGroup>
          </FormRow>

          {/* Row 4: Asset Sub Class, Currency, Submitted By */}
          <FormRow>
            <FormGroup>
              <Label htmlFor="assetSubClass">Asset Sub Class*</Label>
              <Select
                id="assetSubClass"
                value={formData.assetSubClass}
                onChange={(e) => handleInputChange('assetSubClass', e.target.value)}
                className={errors.assetSubClass ? 'error' : ''}
                disabled={isFieldDisabled('assetSubClass') || !formData.assetClass || (!!formData.cusip && !!formData.assetSubClass)}
              >
                <option value="">Select</option>
                {formData.assetSubClass && !dimensionData.assetSubClasses.some((asc: any) => asc.assetSubClassName === formData.assetSubClass) && (
                  <option value={formData.assetSubClass}>{formData.assetSubClass}</option>
                )}
                {dimensionData.assetSubClasses.map((asc: any) => (
                    <option key={asc.id} value={asc.assetSubClassName}>
                      {asc.assetSubClassName}
                    </option>
                  ))}
              </Select>
              {errors.assetSubClass && <FieldError>{errors.assetSubClass}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="currency">Currency*</Label>
              <Select
                id="currency"
                value={formData.currency}
                onChange={(e) => handleInputChange('currency', e.target.value)}
                disabled={isFieldDisabled('currency') || !!formData.cusip} // Auto-populated from security
              >
                <option value="">{formData.cusip ? 'Auto-filled from Security' : 'Select Currency'}</option>
                {dimensionData.currencies.map((currency: any) => (
                  <option key={currency.id} value={currency.currencyCode}>
                    {currency.currencyCode} - {currency.currencyName}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="submittedBy">Submitted By</Label>
              <Input
                id="submittedBy"
                type="text"
                value={formData.submittedBy}
                onChange={(e) => handleInputChange('submittedBy', e.target.value)}
                disabled={isFieldDisabled('submittedBy')}
                placeholder="Enter submitted by"
              />
            </FormGroup>
          </FormRow>

          {/* SECTION: Account & Amount */}
          <TicketSectionTitle>Account & Amount</TicketSectionTitle>
          <FormRow>
            {!useAllocations && (
              isRetail ? (
                /* Retail: Household + Client Account */
                <>
                  <FormGroup>
                    <Label htmlFor="household">Household</Label>
                    <Select
                      id="household"
                      value={selectedHousehold}
                      onChange={(e) => setSelectedHousehold(e.target.value)}
                      disabled={isFieldDisabled('portfolio')}
                    >
                      <option value="">All Households</option>
                      {households.map((household: any) => (
                        <option key={household.id} value={household.id.toString()}>
                          {household.householdName || household.name}
                        </option>
                      ))}
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="portfolio">Client Account*</Label>
                    <Select
                      id="portfolio"
                      value={formData.portfolio}
                      onChange={(e) => selectPortfolio(e.target.value)}
                      className={errors.portfolio ? 'error' : ''}
                      disabled={isFieldDisabled('portfolio')}
                    >
                      <option value="">Select Account</option>
                      {(selectedHousehold ? filteredPortfolios : dimensionData.portfolios).map((p: any) => (
                        <option key={p.id} value={p.portfolioName}>
                          {p.portfolioName}
                        </option>
                      ))}
                    </Select>
                    {errors.portfolio && <FieldError>{errors.portfolio}</FieldError>}
                  </FormGroup>
                </>
              ) : (
                /* Institutional: Portfolio */
                <FormGroup>
                  <Label htmlFor="portfolio">Portfolio*</Label>
                  <Select
                    id="portfolio"
                    value={formData.portfolio}
                    onChange={(e) => selectPortfolio(e.target.value)}
                    className={errors.portfolio ? 'error' : ''}
                    disabled={isFieldDisabled('portfolio')}
                  >
                    <option value="">Select Portfolio</option>
                    {dimensionData.portfolios.map((p: any) => (
                      <option key={p.id} value={p.portfolioName}>
                        {p.portfolioName}
                      </option>
                    ))}
                  </Select>
                  {errors.portfolio && <FieldError>{errors.portfolio}</FieldError>}
                </FormGroup>
              )
            )}

            <FormGroup>
              <Label htmlFor="amount">Par Amount*</Label>
              <Input
                id="amount"
                type="text"
                value={formData.amount}
                onChange={(e) => handleInputChange('amount', e.target.value)}
                onBlur={(e) => {
                  const parsed = parseAmountShorthand(e.target.value);
                  if (parsed > 0) {
                    setFormData((prev: any) => ({ ...prev, amount: formatAmountWithCommas(parsed) }));
                  }
                  handleInputBlur('amount', parsed.toString());
                }}
                placeholder="Enter par amount (e.g., 2MM)"
                className={errors.amount ? 'error' : ''}
                disabled={isFieldDisabled('amount')}
              />
              {errors.amount && <FieldError>{errors.amount}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="price">Clean Price{transactionStatus === 'SENT' ? '*' : ''}</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => handleInputChange('price', e.target.value)}
                placeholder="Price per 100"
                className={errors.price ? 'error' : ''}
                disabled={isFieldDisabled('price') || pricingMode === 'yield'}
              />
              {errors.price && <FieldError>{errors.price}</FieldError>}
            </FormGroup>
          </FormRow>
          </WorkspaceBand>

          <FullWidthSection>
            <AnalyticsExpanderHeader onClick={() => setAnalyticsExpanded(prev => !prev)}>
              {analyticsExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              <span>Price / Yield & Portfolio Impact</span>
            </AnalyticsExpanderHeader>

            {analyticsExpanded && (
              <ExpanderContent>
                <AnalyticsPanelStack>
          {/* IBOR Bond Pricing Panel */}
          <PricingPanel>
            <BandHeader>
              <BandTitleGroup>
                <BandEyebrow><Calculator size={14} /> Price / Yield</BandEyebrow>
                <BandTitle>Bond pricing workbench</BandTitle>
                <BandSubline>Calculate price from target yield, or yield and settlement from clean price.</BandSubline>
              </BandTitleGroup>
              <ScopePills>
                <ScopePill><Percent size={13} /> YTM</ScopePill>
                <ScopePill><Calculator size={13} /> Settlement</ScopePill>
              </ScopePills>
            </BandHeader>

            <PricingWorkbenchGrid>
              <PricingInputPanel>
                <PricingToggle>
                  <ToggleOption
                    type="button"
                    active={pricingMode === 'price'}
                    onClick={() => setPricingMode('price')}
                  >
                    <Calculator size={14} /> Price to Yield
                  </ToggleOption>
                  <ToggleOption
                    type="button"
                    active={pricingMode === 'yield'}
                    onClick={() => setPricingMode('yield')}
                  >
                    <Percent size={14} /> Yield to Price
                  </ToggleOption>
                </PricingToggle>

                {!formData.cusip && (
                  <InfoNote>
                    <Info size={16} /> Select a CUSIP to enable bond analytics.
                  </InfoNote>
                )}

                {pricingMode === 'yield' ? (
                  <FormGroup>
                    <Label htmlFor="yield">Target Yield to Maturity (%)</Label>
                    <InputWithCalc>
                      <Input
                        id="yield"
                        type="number"
                        step="0.001"
                        value={yieldValue}
                        onChange={(e) => setYieldValue(e.target.value)}
                        placeholder="e.g. 5.125"
                        disabled={isFieldDisabled('price') || !formData.cusip}
                      />
                      <CalcButton
                        type="button"
                        onClick={async () => {
                          const calculatedPrice = await fetchBondPricing(
                            formData.cusip,
                            null,
                            yieldValue,
                            formData.settleDate,
                            formData.amount || '1000000'
                          );
                          if (calculatedPrice) {
                            handleInputChange('price', calculatedPrice.toFixed(4));
                          }
                        }}
                        disabled={!formData.cusip || !yieldValue || pricingLoading}
                      >
                        {pricingLoading ? <RefreshCw size={14} /> : <Calculator size={14} />}
                        {pricingLoading ? 'Calculating' : 'Calculate Price'}
                      </CalcButton>
                    </InputWithCalc>
                    <PricingSubValue>Calculated clean price will populate the ticket price.</PricingSubValue>
                  </FormGroup>
                ) : (
                  <FormGroup>
                    <Label htmlFor="priceCalculator">Clean Price Input</Label>
                    <InputWithCalc>
                      <Input
                        id="priceCalculator"
                        type="number"
                        step="0.01"
                        value={formData.price}
                        onChange={(e) => handleInputChange('price', e.target.value)}
                        placeholder="Price per 100"
                        disabled={isFieldDisabled('price')}
                      />
                      <CalcButton
                        type="button"
                        onClick={() => fetchBondPricing(
                          formData.cusip,
                          formData.price,
                          null,
                          formData.settleDate,
                          formData.amount || '1000000'
                        )}
                        disabled={!formData.cusip || !formData.price || pricingLoading}
                      >
                        {pricingLoading ? <RefreshCw size={14} /> : <Calculator size={14} />}
                        {pricingLoading ? 'Calculating' : 'Calculate Yield'}
                      </CalcButton>
                    </InputWithCalc>
                    {formData.cusip && !formData.price && (
                      <PricingSubValue>Enter clean price to calculate yield, accrued interest, dirty price and settlement.</PricingSubValue>
                    )}
                  </FormGroup>
                )}

                {formData.cusip && pricingData?.error && (
                  <ErrorMessage style={{ marginTop: 14 }}>{pricingData.error}</ErrorMessage>
                )}
              </PricingInputPanel>

              <div>
                {formData.cusip && pricingData && !pricingData.error ? (
                  <>
                    <PricingGrid>
                      <PricingCard>
                        <PricingLabel>Clean Price</PricingLabel>
                        <PricingValue>{pricingData.cleanPrice?.toFixed(4) || 'N/A'}</PricingValue>
                        <PricingSubValue>{pricingData.calculatedFrom ? `From ${pricingData.calculatedFrom}` : 'Ticket input'}</PricingSubValue>
                      </PricingCard>

                      <PricingCard>
                        <PricingLabel>Yield to Maturity</PricingLabel>
                        <PricingValue highlight>{pricingData.yieldToMaturity?.toFixed(4) || 'N/A'}%</PricingValue>
                        <PricingSubValue>{pricingData.dayCountConvention || '30/360'}</PricingSubValue>
                      </PricingCard>

                      <PricingCard>
                        <PricingLabel>Accrued Interest</PricingLabel>
                        <PricingValue>{formatCurrencyValue(pricingData.accruedInterest, 2)}</PricingValue>
                        <PricingSubValue>Per 100: {pricingData.accruedInterestPer100?.toFixed(6) || '0'}</PricingSubValue>
                      </PricingCard>

                      <PricingCard>
                        <PricingLabel>Dirty Price</PricingLabel>
                        <PricingValue>{pricingData.dirtyPrice?.toFixed(4) || 'N/A'}</PricingValue>
                        <PricingSubValue>Clean plus accrued</PricingSubValue>
                      </PricingCard>
                    </PricingGrid>

                    <SettlementBreakdown>
                      <PricingTitle style={{ marginBottom: 12 }}><Landmark size={15} /> Settlement Breakdown</PricingTitle>
                      <BreakdownRow>
                        <span>Par Amount</span>
                        <span>{formatCurrencyValue(pricingData.parAmount)}</span>
                      </BreakdownRow>
                      <BreakdownRow>
                        <span>Principal at Clean Price</span>
                        <span>{formatCurrencyValue(pricingData.principalAmount, 2)}</span>
                      </BreakdownRow>
                      <BreakdownRow>
                        <span>Accrued Interest</span>
                        <span>+ {formatCurrencyValue(pricingData.accruedInterest, 2)}</span>
                      </BreakdownRow>
                      <BreakdownRow total>
                        <span>Total Settlement ({formData.transactionType === 'BUY' ? 'Pay' : 'Receive'})</span>
                        <span>{formatCurrencyValue(pricingData.settlementAmount, 2)}</span>
                      </BreakdownRow>
                    </SettlementBreakdown>
                  </>
                ) : (
                  <InfoNote>
                    <Info size={16} /> Pricing output will appear here after calculation. The ticket still saves clean price and par amount even if analytics are not run.
                  </InfoNote>
                )}
              </div>
            </PricingWorkbenchGrid>
          </PricingPanel>

          {/* Portfolio Duration Impact Panel */}
          <DurationImpactPanel>
            <BandHeader>
              <BandTitleGroup>
                <BandEyebrow><Gauge size={14} /> Portfolio Impact</BandEyebrow>
                <BandTitle>Duration preview</BandTitle>
                <BandSubline>Optional risk check against the selected portfolio. Run it when the trade economics are ready.</BandSubline>
              </BandTitleGroup>
              <CalcButton
                type="button"
                onClick={() => fetchDurationImpact(formData, dimensionData)}
                disabled={!canCalculateDuration || durationLoading}
              >
                {durationLoading ? <RefreshCw size={14} /> : <TrendingUp size={14} />}
                {durationLoading ? 'Calculating' : 'Run Impact'}
              </CalcButton>
            </BandHeader>

            {!canCalculateDuration && (
              <InfoNote>
                <Info size={16} /> Enter {missingDurationFields.join(', ')} to enable duration and DV01 impact preview.
              </InfoNote>
            )}

            {canCalculateDuration && durationLoading && (
              <LoadingMessage>Calculating duration impact{formData.price ? '' : ' using available market price source'}...</LoadingMessage>
            )}

            {canCalculateDuration && durationError && (
              <ErrorMessage>{durationError}</ErrorMessage>
            )}

            {canCalculateDuration && !durationLoading && !durationError && !durationImpact && (
              <InfoNote>
                <Info size={16} /> Duration impact is not run automatically. Click Run Impact to preview current duration, pro-forma duration, and change.
              </InfoNote>
            )}

            {canCalculateDuration && durationImpact && !durationLoading && !durationError && (
              <>
                {durationImpact.priceSource && (
                  <PriceSourceCard style={{ marginBottom: 12 }}>
                    <DurationLabel>Pricing Source Used For Impact</DurationLabel>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <PriceSourceBadge source={durationImpact.priceSource}>
                        {durationImpact.priceSource}
                      </PriceSourceBadge>
                      {durationImpact.effectivePrice != null && (
                        <DurationValue style={{ fontSize: 17 }}>
                          {parseFloat(durationImpact.effectivePrice).toFixed(4)}
                        </DurationValue>
                      )}
                    </div>
                    {durationImpact.providedPrice != null && durationImpact.priceSource !== 'Provided' && (
                      <PricingSubValue>Your price: {parseFloat(durationImpact.providedPrice).toFixed(2)} was not the selected impact price.</PricingSubValue>
                    )}
                    {durationImpact.portfolioName && (
                      <PricingSubValue>Portfolio: {durationImpact.portfolioName}</PricingSubValue>
                    )}
                  </PriceSourceCard>
                )}

                <DurationGrid>
                  <DurationCard>
                    <DurationLabel>Current Duration</DurationLabel>
                    <DurationValue>
                      {durationImpact.currentDuration != null
                        ? parseFloat(durationImpact.currentDuration).toFixed(4)
                        : 'N/A'}
                    </DurationValue>
                    {durationImpact.currentPortfolioValue != null && (
                      <PricingSubValue>MV: {formatCurrencyValue(parseFloat(durationImpact.currentPortfolioValue))}</PricingSubValue>
                    )}
                  </DurationCard>

                  <DurationCard>
                    <DurationLabel>Pro-forma Duration</DurationLabel>
                    <DurationValue>
                      {durationImpact.newDuration != null
                        ? parseFloat(durationImpact.newDuration).toFixed(4)
                        : 'N/A'}
                    </DurationValue>
                    {durationImpact.newPortfolioValue != null && (
                      <PricingSubValue>MV: {formatCurrencyValue(parseFloat(durationImpact.newPortfolioValue))}</PricingSubValue>
                    )}
                  </DurationCard>

                  <DurationCard>
                    <DurationLabel>Duration Change</DurationLabel>
                    {durationImpact.durationChange != null ? (
                      <>
                        <DurationValue
                          positive={parseFloat(durationImpact.durationChange) > 0}
                          negative={parseFloat(durationImpact.durationChange) < 0}
                        >
                          {parseFloat(durationImpact.durationChange) > 0 ? '+' : ''}
                          {parseFloat(durationImpact.durationChange).toFixed(4)}
                        </DurationValue>
                        {durationImpact.durationChangePercent != null && (
                          <DurationChange
                            positive={parseFloat(durationImpact.durationChangePercent) > 0}
                            negative={parseFloat(durationImpact.durationChangePercent) < 0}
                          >
                            {parseFloat(durationImpact.durationChangePercent) > 0 ? '+' : ''}
                            {parseFloat(durationImpact.durationChangePercent).toFixed(2)}%
                          </DurationChange>
                        )}
                      </>
                    ) : (
                      <DurationValue>N/A</DurationValue>
                    )}
                  </DurationCard>

                  {durationImpact.securityDuration != null && (
                    <DurationCard>
                      <DurationLabel>Security Duration</DurationLabel>
                      <DurationValue>{parseFloat(durationImpact.securityDuration).toFixed(4)}</DurationValue>
                      <PricingSubValue>{durationImpact.securityName}</PricingSubValue>
                    </DurationCard>
                  )}
                </DurationGrid>
              </>
            )}
          </DurationImpactPanel>
                </AnalyticsPanelStack>
              </ExpanderContent>
            )}
          </FullWidthSection>
        </FixedIncomeWorkspace>
        );
      }}
    </BaseTicketScreen>
  );
};

export default FixedIncomeTicketScreen;
